import type { Metrics, VendorData } from './dashboard';
import type { Sale, Snapshot } from './data';
import { type Filters, build, days, filterStayRows, sourceInclusionDelta, totals } from './model';
import regions from './regions.json';

export function effectiveStart(s: Snapshot, offer: string, fallback: string) {
  const rows = s.campaigns.filter((c) => c.offer_bk === offer);
  const starts = rows
    .map((c) => c.start_date)
    .filter(Boolean)
    .sort();
  const sales = rows
    .map((c) => c.first_sale_date)
    .filter((date): date is string => !!date)
    .sort();
  return sales.length && starts.length ? [starts[0], sales[0]].sort()[1] : fallback;
}

export function buildVendorData(s: Snapshot, selected: Filters): VendorData {
  const clean = (v: ReturnType<typeof totals>): Metrics =>
    Object.fromEntries(
      ['nights', 'revenue', 'orders', 'packages', 'alos', 'lead', 'upgrade', 'adr', 'inclusions'].map((k) => [
        k,
        v[k as keyof typeof v],
      ]),
    ) as Metrics;

  // The source room sort follows the source package-price rank, retaining literal room names.
  const base = selected;
  const currentStart = effectiveStart(s, base.current, base.from),
    comparisonStart = effectiveStart(s, base.comparison, base.cmpFrom);

  const roomOrder = (offer: string) => [
    ...new Set(
      s.sales
        .filter((r) => r.offer_bk === offer)
        .sort((a, b) => (a.package_price_rank ?? Infinity) - (b.package_price_rank ?? Infinity))
        .map((r) => r.room_type_name ?? 'Unknown'),
    ),
  ];
  const currentOrder = roomOrder(base.current),
    cmpOrder = roomOrder(base.comparison);
  const subRegion = (r: Sale) => {
    if (!['AUSTRALIA', 'AU', 'AUS'].includes((r.country_name ?? '').trim().toUpperCase())) {
      return ' ';
    }
    const raw = r.customer_geo_state ?? '';
    const patterns: Array<[string, RegExp]> = [
      ['NSW', /(\bNEW\b\s*\bSOUTH\b\s*\bWALES\b|\bNSW\b|N\s*S\s*W)/],
      ['VIC', /(\bVICTORIA\b|\bVIC\b|V\s*I\s*C)/],
      ['QLD', /(\bQUEENSLAND\b|\bQLD\b|Q\s*L\s*D)/],
      ['SA', /(\bSOUTH\b\s*\bAUSTRALIA\b|\bSA\b|S\s*A)/],
      ['WA', /(\bWESTERN\b\s*\bAUSTRALIA\b|\bWA\b|W\s*A)/],
      ['TAS', /(\bTASMANIA\b|\bTAS\b|T\s*A\s*S)/],
      ['ACT', /(\bAUSTRALIAN\b\s*\bCAPITAL\b\s*\bTERRITORY\b|\bACT\b|A\s*C\s*T)/],
      ['NT', /(\bNORTHERN\b\s*\bTERRITORY\b|\bNT\b|N\s*T)/],
    ];
    return patterns.find(([, re]) => re.test(raw))?.[0] ?? 'Other (AU)';
  };
  const variants: VendorData['variants'] = [];
  // Short campaigns often make Full/30/60/90-day windows identical. Compute
  // each actual population once; the period label still has its own variant.
  const computedWindows = new Map<string, VendorData['variants'][number]>();
  const checkInWeekdays = new Map<string, string>();
  const weekdays = ['Unknown', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekday = (r: Sale) => {
    if (!r.check_in_date) return 'Unknown';
    const cached = checkInWeekdays.get(r.check_in_date);
    if (cached) return cached;
    const value = weekdays[((new Date(r.check_in_date + 'T00:00:00Z').getUTCDay() + 6) % 7) + 1];
    checkInWeekdays.set(r.check_in_date, value);
    return value;
  };
  const fixedRevenue = (rows: Array<Sale>, currency: string) => {
    const keys = new Set(rows.map((r) => JSON.stringify([r.orderKey, r.itemKey])));
    const records = (s.history || []).filter((r) => keys.has(JSON.stringify([r.orderKey, r.itemKey])));
    const costs = records
      .map((r) => (currency === 'AUD' ? r.cost_sales_aud : r.cost_sales))
      .filter((x): x is number => x !== null);
    const holds = records
      .map((r) => (currency === 'AUD' ? r.vendor_holdback_amount_aud : r.vendor_holdback_amount))
      .filter((x): x is number => x !== null);
    return costs.length && holds.length ? costs.reduce((a, b) => a + b, 0) - holds.reduce((a, b) => a + b, 0) : null;
  };
  const vendorCurrency =
    s.sales
      .filter((row) => row.offer_bk === base.current && row.date >= base.from && row.date <= base.to)
      .map((row) => row.cost_currency)
      .filter((value): value is string => !!value)
      .sort()
      .pop() || 'AUD';
  for (const currency of [...new Set(['AUD', vendorCurrency])]) {
    for (const refund of ['Before Refund', 'After Refund'] as const) {
      for (const period of ['Full Period', 'First 30 Days', 'First 60 Days', 'First 90 Days']) {
        const periodDays = period === 'Full Period' ? null : Number(period.split(' ')[1]);
        const end = (from: string, to: string) =>
          periodDays
            ? new Date(
                Math.min(Date.parse(to + 'T00:00:00Z'), Date.parse(from + 'T00:00:00Z') + (periodDays - 1) * 86400000),
              )
                .toISOString()
                .slice(0, 10)
            : to;
        const f = {
          ...base,
          currency,
          refund,
          to: end(currentStart, base.to),
          cmpTo: base.comparison ? end(comparisonStart, base.cmpTo) : base.cmpTo,
        };
        const windowKey = JSON.stringify([currency, refund, f.to, f.cmpTo]);
        const cached = computedWindows.get(windowKey);
        if (cached) {
          variants.push({ ...cached, period });
          continue;
        }
        const m = build(s, f, false);
        const cur = m.current.rows,
          cmp = m.comparison.rows;
        const windowCurrentRevenue = m.current.totals.revenue;
        m.current.totals.revenue = fixedRevenue(cur, currency);
        const group = (rows: Array<Sale>, key: (r: Sale) => string) =>
          [...new Set(rows.map(key))].map((name) => ({
            name,
            ...clean(
              totals(
                rows.filter((r) => key(r) === name),
                [],
                f,
              ),
            ),
          }));
        const byRoom = (rows: Array<Sale>, order: Array<string>) =>
          group(rows, (r) => r.room_type_name ?? 'Unknown').sort((a, b) => {
            const ia = order.indexOf(a.name),
              ib = order.indexOf(b.name);
            return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib) || a.name.localeCompare(b.name);
          });
        const countries = m.customers
          .map((g) => ({
            name: g.name,
            region: regions[g.name] ?? g.name,
            current: clean(g.current),
            comparison: clean(g.comparison),
          }))
          .sort((a, b) => a.region.localeCompare(b.region) || a.name.localeCompare(b.name));
        const dow = weekdays.map((name) => ({
          name,
          current: totals(
            cur.filter((r) => weekday(r) === name),
            [],
            f,
          ).packages,
          comparison: totals(
            cmp.filter((r) => weekday(r) === name),
            [],
            f,
          ).packages,
        }));
        const minYear = (rows: Array<Sale>) =>
          Math.min(...rows.filter((r) => r.check_in_date).map((r) => Number(r.check_in_date.slice(0, 4))));
        const month = (r: Array<Sale>, year: number) => {
          const out = new Map<string, Array<Sale>>();
          for (const row of r) {
            const key = row.check_in_date
              ? `${Number(row.check_in_date.slice(0, 4)) - year + 1}-${row.check_in_date.slice(5, 7)}`
              : 'Unknown';
            const bucket = out.get(key);
            if (bucket) {
              bucket.push(row);
            } else {
              out.set(key, [row]);
            }
          }
          return out;
        };
        const currentStays = filterStayRows(cur, f.booking, s.meta.bookingAsOf ?? s.meta.asOf),
          comparisonStays = filterStayRows(cmp, f.booking, s.meta.bookingAsOf ?? s.meta.asOf);
        const ca = month(currentStays, minYear(currentStays)),
          cb = month(comparisonStays, minYear(comparisonStays));
        // Saved Check-In by Month worksheet uses Bookings (AR), regardless of refund control.
        const monthly = [...new Set([...ca.keys(), ...cb.keys()])]
          .sort((a, b) =>
            a === 'Unknown'
              ? -1
              : b === 'Unknown'
                ? 1
                : Number(a.split('-')[0]) * 12 +
                  Number(a.split('-')[1]) -
                  Number(b.split('-')[0]) * 12 -
                  Number(b.split('-')[1]),
          )
          .map((key) => ({
            name:
              key === 'Unknown'
                ? 'Unknown'
                : `Y${key.split('-')[0]} ${new Date('2000-' + key.split('-')[1] + '-01T00:00:00Z').toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`,
            current: totals(ca.get(key) ?? [], [], { ...f, refund: 'After Refund' }).orders,
            comparison: totals(cb.get(key) ?? [], [], { ...f, refund: 'After Refund' }).orders,
          }));
        const roomNames = byRoom(cur, currentOrder).map((r) => r.name);
        const nightCurve = (rows: Array<Sale>, from: string, to: string) => {
          const daily = new Map<number, { total: number; known: number }>();
          for (const row of rows) {
            const value = row.number_of_nights;
            if (
              value === null ||
              (row.transaction_side !== 'sale' && !(refund === 'After Refund' && row.transaction_side === 'refund'))
            ) {
              continue;
            }
            const day = days(from, row.date),
              previous = daily.get(day) || { total: 0, known: 0 };
            daily.set(day, {
              total: previous.total + (row.transaction_side === 'sale' ? value : -value),
              known: previous.known + 1,
            });
          }
          let total = 0,
            known = 0;
          return Array.from({ length: days(from, to) + 1 }, (_, day) => {
            const next = daily.get(day);
            total += next?.total || 0;
            known += next?.known || 0;
            return known ? total : null;
          });
        };
        const byRoomCurve = roomNames.map((name) =>
          nightCurve(
            cur.filter((r) => (r.room_type_name ?? 'Unknown') === name),
            currentStart,
            f.to,
          ),
        );
        const roomCurve = Array.from({ length: days(currentStart, f.to) + 1 }, (_, day) => ({
          day,
          ...Object.fromEntries(roomNames.map((_, i) => ['r' + i, byRoomCurve[i][day]])),
        }));
        const currentCurve = nightCurve(cur, currentStart, f.to),
          comparisonCurve = base.comparison ? nightCurve(cmp, comparisonStart, f.cmpTo) : [];
        const curve = Array.from({ length: Math.max(currentCurve.length, comparisonCurve.length) }, (_, day) => ({
          day,
          current: currentCurve[day] ?? null,
          comparison: comparisonCurve[day] ?? null,
        }));
        const bins = [
          'Unknown',
          '0–30',
          '31–60',
          '61–90',
          '91–120',
          '121–150',
          '151–180',
          '181–210',
          '211–240',
          '241–270',
          '271–300',
          '301+',
        ];
        const leadBucket = (r: Sale) => {
          const value = r.order_date && r.check_in_date ? days(r.order_date, r.check_in_date) : null;
          return value === null || value < 0
            ? 'Unknown'
            : value > 300
              ? '301+'
              : value <= 30
                ? '0–30'
                : `${Math.floor((value - 1) / 30) * 30 + 1}–${(Math.floor((value - 1) / 30) + 1) * 30}`;
        };
        const leadtime = bins.map((name) => ({
          name,
          current: totals(
            cur.filter((r) => leadBucket(r) === name),
            [],
            f,
          ).orders,
          comparison: totals(
            cmp.filter((r) => leadBucket(r) === name),
            [],
            f,
          ).orders,
        }));
        const sourceCurrency = (id: string, from: string, to: string) =>
          s.sales
            .filter((r) => r.offer_bk === id && r.date >= from && r.date <= to)
            .map((r) => r.cost_currency)
            .filter((value): value is string => value !== null)
            .sort()
            .pop();
        const currentCurrency = sourceCurrency(base.current, f.from, f.to),
          comparisonCurrency = sourceCurrency(base.comparison, f.cmpFrom, f.cmpTo);
        const variant: VendorData['variants'][number] = {
          effectiveCurrentStart: currentStart,
          effectiveComparisonStart: comparisonStart,
          currencyMismatch:
            currency !== 'AUD' && !!currentCurrency && !!comparisonCurrency && currentCurrency !== comparisonCurrency,
          hasInclusions: cur.some((r) => r.inclusion_upgrade_flag),
          inclusionDelta: sourceInclusionDelta(cur, cmp),
          currency,
          refund,
          period,
          to: f.to,
          cmpTo: f.cmpTo,
          windowCurrentRevenue,
          current: clean(m.current.totals),
          comparison: clean(m.comparison.totals),
          curve,
          roomNames,
          roomCurve,
          rooms: { current: byRoom(cur, currentOrder), comparison: byRoom(cmp, cmpOrder) },
          countries,
          states: group(cur, (r) => (r.country_name ?? 'Unknown') + '|' + subRegion(r)).map((r) => ({
            ...r,
            country: r.name.split('|')[0],
            state: r.name.split('|')[1],
          })),
          dow,
          dowBookings: weekdays.map((name) => ({
            name,
            current: totals(
              cur.filter((r) => weekday(r) === name),
              [],
              f,
            ).orders,
            comparison: totals(
              cmp.filter((r) => weekday(r) === name),
              [],
              f,
            ).orders,
          })),
          monthly,
          leadtime,
        };
        computedWindows.set(windowKey, variant);
        variants.push(variant);
      }
    }
  }
  const report = {
    property: s.sales.find((r) => r.offer_bk === base.current)?.vendor_name || 'Campaign report',
    source: 'Campaign sales and refunds',
    asOf: s.meta.asOf,
    prepared: s.meta.extractedAt.slice(0, 10),
    workbook: 'Vendor Report – Flash – Post Campaign',
    revision: '8.2',
    current: {
      name: s.campaigns.find((c) => c.offer_bk === base.current)?.offer_opportunity_name || 'Current campaign',
      from: base.from,
      to: base.to,
    },
    comparison: {
      name: s.campaigns.find((c) => c.offer_bk === base.comparison)?.offer_opportunity_name || 'No comparison',
      from: base.cmpFrom,
      to: base.cmpTo,
    },
    variants,
  };

  return report;
}
